import { IContentLibraryState } from '../store/content-library.types';
import { ECreateOption } from '../types/enums/create-option';
import { CreateDocumentConstants } from '../create-document/constants/create-document.constants';
import { EResourceSortAttribute } from '@/api/resources/types/enum/resource-sort-attribute';
import { ESortOrder } from '@/api/resources/types/enum/sort-order';
import { EResourceType } from '@/api/resources/types/enum/resource-type';
import { IResource } from '@/api/resources/types/resource';
import { IContentLinkBlock } from '@/content-link/types/content-link';
import { translate } from '@/helpers/translation.helper';
import { IDocument } from '@/types/Document';
import { IVideo } from '@/types/Video';
import { IOptionMenuItem } from '@/dealroom/types/option-menu-item';
import { IDocumentPage } from '@/types/document-page';
import { EDocumentPageType } from '@/enums/document-page-type';
import { EnumAppMode } from '@/enums/AppMode';
import { EExternalEditorType } from '@/enums/ExternalEditorType';
import { IEditorBlock } from '@/editor/types';

export class ContentLibraryHelper {
  static getThumbUrl(resource: IResource): string {
    if (!resource.block) {
      return null;
    }

    let url = '';
    switch (resource.resourceType) {
      case EResourceType.ContentLink:
        url = (resource.block as IContentLinkBlock).thumbUrl;
        break;
      case EResourceType.Template:
        url = (resource.block as IDocument).previewThumbUrl;
        break;
      case EResourceType.Video:
        url = (resource.block as IVideo).thumbUrl;
        break;
      case EResourceType.Editor:
        url = (resource.block as IEditorBlock).previewThumbUrl;
        break;
    }
    return url || this.getFallbackThumbUrl(resource.resourceType);
  }

  static getFallbackThumbUrl(resourceType: EResourceType): string {
    switch (resourceType) {
      case EResourceType.ContentLink:
        return '/assets/img/content-link-resource-placeholder.svg';
      case EResourceType.Template:
        return '/assets/img/template-resource-placeholder.svg';
      case EResourceType.Video:
        return '/assets/img/video-resource-placeholder.svg';
      case EResourceType.Editor:
        return '/assets/img/editor-resource-placeholder.svg';
    }
  }

  static getIcon(resource: IResource): string {
    const isGoogleDocsTemplate =
      (resource?.block as IDocument)?.externalEditorType === EExternalEditorType.Docs;

    switch (resource.resourceType) {
      case EResourceType.ContentLink:
        return 'link-block';
      case EResourceType.Template:
        return isGoogleDocsTemplate ? 'google-drive-black' : 'template-black';
      case EResourceType.Video:
        return 'video-block';
      case EResourceType.Editor:
        return 'editor-block';
    }
  }

  static getMetaData(resource: IResource): string {
    let metaData: string = null;
    switch (resource.resourceType) {
      case EResourceType.ContentLink: {
        metaData = translate('link').toString();
        break;
      }
      case EResourceType.Template: {
        metaData = translate('template').toString();
        break;
      }
      case EResourceType.Video: {
        metaData = translate('video').toString();
        break;
      }
      case EResourceType.Editor: {
        metaData = translate('editor').toString();
        break;
      }
    }
    const usageMessage = this.getUsageMessage(resource);

    return usageMessage ? `${metaData} | ${usageMessage}` : metaData;
  }

  static getUsageMessage(resource: IResource): string {
    const hasUsedCount = resource.statistics?.usedCount;
    if (hasUsedCount === undefined) {
      return '';
    }

    return translate('used-in-documents', {
      count: resource.statistics?.usedCount,
    }).toString();
  }

  static getButtonText(attribute: EResourceSortAttribute): string {
    switch (attribute) {
      case EResourceSortAttribute.Title:
        return 'name';
      case EResourceSortAttribute.UpdatedAt:
        return 'updated-at';
      case EResourceSortAttribute.CreatedAt:
        return 'created-at';
      case EResourceSortAttribute.LastUsed:
        return 'last-used';
    }
  }

  static getFilterText(type?: EResourceType): string {
    switch (type) {
      case EResourceType.ContentLink:
        return 'content-links';
      case EResourceType.Editor:
        return 'editors';
      case EResourceType.Template:
        return 'templates';
      case EResourceType.Video:
        return 'videos';
      default:
        return 'all-resources';
    }
  }

  static getDefaultStateWithFilters(
    state: IContentLibraryState,
    defaultState: IContentLibraryState
  ): IContentLibraryState {
    return {
      ...defaultState,
      payload: {
        ...defaultState.payload,
        filter: state.payload.filter,
      },
    };
  }

  static getSortOptionsWithIcon(
    icon: string,
    sortOptions: IOptionMenuItem[],
    selectedOption: EResourceSortAttribute
  ): IOptionMenuItem[] {
    return sortOptions.map((option: IOptionMenuItem) => ({
      ...option,
      icon: option.title === this.getButtonText(selectedOption) ? icon : '',
    }));
  }

  static getTooltipText(sortAttribute: EResourceSortAttribute, sortOrder: ESortOrder): string {
    switch (sortAttribute) {
      case EResourceSortAttribute.CreatedAt:
      case EResourceSortAttribute.LastUsed:
      case EResourceSortAttribute.UpdatedAt:
        return sortOrder === ESortOrder.Asc ? 'sort-by-time-asc' : 'sort-by-time-desc';
      default:
        return sortOrder === ESortOrder.Asc ? 'sort-by-alphanum-asc' : 'sort-by-alphanum-desc';
    }
  }

  static addResourceDetails(stateResources: IResource[], resource: IResource): IResource[] {
    if (!stateResources || !stateResources.length) {
      return [resource];
    }
    const resourceExists: boolean = stateResources.some(
      (stateResource: IResource) => stateResource.id === resource.id
    );
    if (!resourceExists) {
      return [resource, ...stateResources];
    }
    return stateResources.map((stateResource: IResource) =>
      stateResource.id === resource.id ? resource : stateResource
    );
  }

  static isInstantlyCreatedResource(type: EResourceType): boolean {
    return [EResourceType.Template, EResourceType.Editor].includes(type);
  }

  static getParamFromResourceType(type: EResourceType): string {
    switch (type) {
      case EResourceType.Template:
        return 'template';
      case EResourceType.ContentLink:
        return 'content-link';
      case EResourceType.Video:
        return 'video';
      case EResourceType.Editor:
        return 'editor';
    }
  }

  static getResourceTypeFromParam(param: string): EResourceType {
    switch (param) {
      case 'template':
        return EResourceType.Template;
      case 'content-link':
        return EResourceType.ContentLink;
      case 'video':
        return EResourceType.Video;
      case 'editor':
        return EResourceType.Editor;
    }
  }

  static isResourceReactive(type: EResourceType): boolean {
    return type !== EResourceType.Template;
  }

  static getUpdatePagePayload(page: IDocumentPage, copyId: string) {
    switch (page.pageType) {
      case EDocumentPageType.Video:
        return {
          id: page.id,
          videoId: copyId,
          pageType: 'video',
        };
      case EDocumentPageType.ContentLink:
        return {
          id: page.id,
          blockId: copyId,
          pageType: 'contentLinkBlock',
        };
      case EDocumentPageType.Editor:
        return {
          id: page.id,
          blockId: copyId,
          pageType: 'editorBlock',
        };
    }
  }

  static getResourceTypeFromCreateOption(option: ECreateOption): EResourceType {
    switch (option) {
      case ECreateOption.ContentLink:
        return EResourceType.ContentLink;
      case ECreateOption.Editor:
        return EResourceType.Editor;
      case ECreateOption.Template:
        return EResourceType.Template;
      case ECreateOption.Video:
        return EResourceType.Video;
    }
  }

  static getActionsForAppMode(appMode: string) {
    if (appMode === EnumAppMode.Sales) {
      return CreateDocumentConstants.salesActions;
    }
    return CreateDocumentConstants.businessActions;
  }
}
